<i18n>
  {
    "en": {
      "emptyData": "No data"
    },
    "ru": {
      "emptyData": "Нет данных"
    }
  }
</i18n>

<template>
    <div class="plan-image">
        <div class="floorPlan">
            <div v-if="!floorPlan.imageFloorUrl" class="plan-upload-floor">
                <div v-if="hasEditPermissions">
                    <div class="upload-title">{{$t('locations.dictionaryItem.roomPlanTitleupload1')}}</div>
                    <div class="upload-title">{{$t('locations.dictionaryItem.roomPlanTitleupload2')}}</div>

                    <DxFileUploader
                        class="btn-upload"
                        :labelText="$t('license.buttons.uploaderDropButtonText')"
                        accept="image/*"
                        :max-file-size="21000000"
                        uploadMode="useForm"
                        @value-changed="onUploaded"
                    />
                </div>

                <div v-else>
                    <span class="emptyData">{{ $t('emptyData') }}</span>
                </div>
            </div>

            <div v-else ref="container" class="viewer" :style="{ cursor: cursorStyle }">
                <svg ref="svg" class="scene-svg" @click="createTag">
                    <g ref="viewport">
                        <image
                            :href="floorPlan.imageFloorUrl"
                            x="0"
                            y="0"
                            :width="img.w"
                            :height="img.h"
                        />

                    </g>
                    <g>
                        <template v-for="device in placedDevices">
                            <circle
                                :key="`${device.id}-hit-area`"
                                class="tag-svg-hit-area"
                                :cx="getPointScreenX(device)"
                                :cy="getPointScreenY(device)"
                                :r="pointHitRadius"
                                @pointerdown="startDrag(device, $event)"
                                @mouseover="onMouseOverTag(device)"
                            />
                            <circle
                                :key="device.id"
                                :cx="getPointScreenX(device)"
                                :cy="getPointScreenY(device)"
                                :r="pointRadius"
                                :class="['tag-svg', getStatusClass(device), { 'point--animated': isAnimatedStatus(device) }]"
                            />
                        </template>
                    </g>
                </svg>

                <div v-if="selected" ref="modalInfo" class="modal-device-info" :style="modalStyle">
                    <div class="device-info">
                        <div>
                            <router-link class="link" :to="{ name: 'device', params: { id: selected.id }}">{{ selected.modelCode }} {{ selected.paperFormat }}</router-link>
                            <span>, (<a class="link" v-bind:href="`https://${selected.ip}`" target="_blank" rel="noopener noreferrer">{{selected.ip}}</a>)</span>
                        </div>

                        <dx-button
                            v-if="hasEditPermissions"
                            icon="close"
                            type="normal"
                            :hint="$t('locations.imageTagger.deleteDevice')"
                            @click="onDelete"
                        />
                    </div>

                    <!-- сейчас на производительность не сильно влияет, возможно переделать на обычный лист -->
                    <dx-data-grid
                        class="consumables"
                        :data-source="selected.parts"
                        :showColumnHeaders="false"
                        :showColumnLines="false"
                    >
                        <dx-column
                            data-field="partCode"
                            :caption="$t('consumables.color')"
                            :width="30"
                            css-class="toner-icon"
                            cell-template="color-toner-cell"
                        />
                        <template #color-toner-cell="{ data }">
                            <div class="fill" :style="getFillStyle(data.value)"></div>
                        </template>

                        <dx-column
                            data-field="partName"
                            :caption="$t('consumables.name')"
                        />
                        <dx-column
                            data-field="level"
                            name="percentLevel"
                            :caption="$t('consumables.level')"
                            :width="100"
                            cell-template="progress-cell"
                        />
                        <template #progress-cell="{ data }">
                            <div class="progress-cell">
                                <DxProgressBar
                                    :class="getClassProgress(data.value)"
                                    :min="0"
                                    :max="100"
                                    :value="data.value <= 4 ? 4 : data.value"
                                    :showStatus="false"
                                />
                            </div>
                        </template>

                        <dx-column
                            data-field="level"
                            :caption="$t('consumables.level')"
                            :width="50"
                            :customize-text="customizeText"
                        />
                    </dx-data-grid>
                </div>
            </div>
        </div>

        <div class="actions">
            <div v-if="hasEditPermissions" :title="unplacedDevices.length > 0 ? $t('locations.imageTagger.addDevice') : $t('locations.imageTagger.noDevices')">
                <aims2-button faIcon="scanner" :disabled="!floorPlan.imageFloorUrl || unplacedDevices.length === 0" @click="showPopup = true" />
            </div>

            <dx-button
                v-if="hasEditPermissions"
                icon="save"
                type="normal"
                :hint="$t('general.save')"
                :disabled="!hasEditPermissions || saving || !locationId || locationId < 0"
                @click="save()"
            />

            <div class="zommer">
                <dx-button
                    icon="plus"
                    type="normal"
                    :disabled="!floorPlan.imageFloorUrl || isZoomInDisabled"
                    @click="zoomIn"
                />
                <dx-button
                    icon="minus"
                    type="normal"
                    :disabled="!floorPlan.imageFloorUrl || isZoomOutDisabled"
                    @click="zoomOut"
                />
                <dx-button
                    icon="fullscreen"
                    type="normal"
                    :disabled="!floorPlan.imageFloorUrl || isResetDisabled"
                    @click="reset"
                />
            </div>

            <div v-if="showPopup" class="add-device-overlay">
                <div class="add-device-window" @click.stop>

                    <div class="popup-header">
                        <div class="popup-title">{{ $t('locations.imageTagger.title') }}</div>

                        <dx-button icon="close" styling-mode="text" @click="showPopup = false" />
                    </div>

                    <div class="popup-content">
                        <DxList
                            class="popup-list"
                            ref="list"
                            :data-source="unplacedDevices"
                            height="300px"
                            key="id"
                            item-template="list-item"
                            :show-selection-controls="false"
                            selection-mode="single"
                            :select-by-click="true"
                            :search-enabled="true"
                            :search-expr="['modelCode', 'ip']"
                            @selection-changed="onOptionChanged"
                        >
                            <template #list-item="{ data }">
                                <div class="device-item">
                                    <span class="status-dot" :class="getStatusClass(data)"></span>
                                    <div class="device-text">{{ data.modelCode }}, ({{ data.ip }})</div>
                                </div>
                            </template>
                        </DxList>
                    </div>
                </div>
            </div>

            <dx-button
                v-if="hasEditPermissions"
                class="plan-delete-btn"
                icon="trash"
                type="normal"
                :hint="$t('locations.imageTagger.deleteImage')"
                :disabled="!floorPlan.imageFloorUrl"
                @click="onDeleteFloorPlan"
            />
        </div>
    </div>
</template>

<script>
import DxDataGrid, { DxColumn } from "devextreme-vue/data-grid";
import { DxProgressBar } from 'devextreme-vue/progress-bar';
import DxList from "devextreme-vue/list";
import { DxButton } from 'devextreme-vue';
import { DxFileUploader } from 'devextreme-vue/file-uploader';

import showConfirmDialog from '@/components/confirm-dialog';
import aims2Button from "@/components/active-button.vue";
import { getFillStyle, getClassProgress } from '@/services/cell-consumables';
import { notifier } from '@/shared/lib/notifier/index';
import { DataBacked } from "@/mixins/dataBackedMixin";
import {
    postfloorPlan,
    putfloorPlan,
    deletefloorPlan,
    putDictionaryItemDevices
} from '@/services/api';

import * as d3 from "d3";

const STATUS_MAP = {
    OK: 'ok',
    WARNING: 'warning',
    SCAN_ERROR: 'warning',
    ERROR: 'error',
    NOT_AVAILABLE: 'error',
    NOT_SERVICED: 'not-serviced'
};

export default {
    name: 'image-tagger',
    props: ['locationId', 'hasEditPermissions', 'floorPlan', 'devices'],
    mixins: [DataBacked],
    components: {
        DxDataGrid, DxColumn, DxProgressBar, DxButton, DxFileUploader, aims2Button, DxList
    },
    data() {
      return {
        svgSelection: null,
        img: { w: 1000, h: 1000 },
        currentTransform: d3.zoomIdentity,
        minScale : 1,
        maxScale: 10,
        selected: null,
        cursorStyle: 'move',
        listSelection: null,
        showPopup: false,
        isConfirmDialogActive: false,
        isDragging: false,
        saving: false,
        initialized: false,
      };
    },
    computed:{
        placedDevices() {
            return this.devices.filter(d => d.xcoordinate != null && d.ycoordinate != null);
        },
        unplacedDevices() {
            return this.devices.filter(d => d.xcoordinate == null || d.ycoordinate == null);
        },

        isZoomInDisabled() {
            return this.currentTransform.k >= this.maxScale;
        },
        isZoomOutDisabled() {
            return this.currentTransform.k <= this.minScale + 0.0001;
        },
        isResetDisabled() {
            return Math.abs(this.currentTransform.k - this.minScale) < 0.0001;
        },
        // радиус видимой точки в SVG
        pointRadius() {
            return Math.max(8, 3 * this.currentTransform.k);
        },
        // радиус невидимой области для drag/mouseover
        pointHitRadius() {
            return Math.max(12, this.pointRadius + 4);
        },

        modalStyle() {
            if (!this.selected || !this.currentTransform) return {};

            const x = this.selected.xcoordinate * this.img.w;
            const y = this.selected.ycoordinate * this.img.h;

            const t = this.currentTransform;

            // масштабируем базовую позицию
            const scaledX = x * t.k;
            const scaledY = y * t.k;

            // размеры modal
            const popupWidth = 350;

            // смещение относительно точки
            const offsetX = this.pointHitRadius; // точка рисуется в screen-space, отступ тоже экранный
            const offsetY = 0; // вертикальное смещение (можно настроить)

            // итоговая позиция точки в viewport
            const pointScreenX = scaledX + t.x;
            const pointScreenY = scaledY + t.y;

            // определяем, поместится ли modal справа от точки
            const svgRect = this.$refs.svg.getBoundingClientRect();
            const availableRightSpace = svgRect ? svgRect.width - pointScreenX : 0;
            const canFitRight = availableRightSpace >= popupWidth + offsetX;

            let screenX, screenY;

            if (canFitRight) {
                // modal справа от точки
                screenX = pointScreenX + offsetX;
                screenY = pointScreenY + offsetY;
            } else {
                // modal слева от точки — учитываем ширину modal
                screenX = pointScreenX - popupWidth - offsetX;
                screenY = pointScreenY + offsetY; // выравниваем по верхней границе точки
            }

            return {
                position: 'absolute',
                left: `${screenX}px`,
                top: `${screenY}px`,
                transform: 'translate(0, 0)', // убираем вертикальное смещение
                width: `${popupWidth}px`,
                overflow: 'auto'
            };
        }
    },
    methods: {
        getFillStyle, getClassProgress,
        customizeText(cellInfo) {
            return cellInfo.value + "%";
        },
        getStatusClass(item) {
            return STATUS_MAP[item.statusCode] || '';
        },
        getPointScreenX(device) {
            const x = device.xcoordinate * this.img.w;

            return this.currentTransform.applyX(x);
        },
        getPointScreenY(device) {
            const y = device.ycoordinate * this.img.h;

            return this.currentTransform.applyY(y);
        },
        isAnimatedStatus(item) {
            const statusClass = this.getStatusClass(item);
            return statusClass === 'warning' || statusClass === 'error';
        },
        zoomIn() {
            if (!this.svgSelection) return;

            this.zoomToPoint(1.2);
        },
        zoomOut() {
            if (!this.svgSelection) return;

            this.zoomToPoint(0.8);
        },
        reset() {
            if (!this.svgSelection) return;

            this.fitToScreen();
        },
        async save(devices = this.devices) {
            if (!this.locationId || this.locationId < 0) return false;

            this.saving = true;
            try {
                let changed = false;

                if (this.floorPlan.fileUploaded && this.floorPlan.imageFloorUrl) {
                    const data = new FormData();
                    data.append("floorPlan", this.floorPlan.fileUploaded);

                    if (this.floorPlan.attr && this.floorPlan.attr.id) {
                        await putfloorPlan(this.locationId, this.floorPlan.attr.id, data);
                    } else {
                        this.floorPlan.attr = await postfloorPlan(this.locationId, data);
                    }

                    this.floorPlan.fileUploaded = null;
                    changed = true;
                }

                if (this.floorPlan.attr && this.floorPlan.attr.id && this.floorPlan.updateDevicesCoordinates) {
                    await putDictionaryItemDevices(devices);
                    this.floorPlan.updateDevicesCoordinates = false;
                    changed = true;
                }

                if (this.floorPlan.attr && this.floorPlan.attr.id && !this.floorPlan.imageFloorUrl) {
                    await deletefloorPlan(this.locationId, this.floorPlan.attr.id);
                    this.floorPlan.attr = null;
                    changed = true;
                }

                if (changed) {
                    notifier.showSuccess(this.$t('general.updateSuccess'));
                } else {
                    notifier.showSuccess(this.$t('general.entitySaveWithoutChanges'));
                }

                this.saving = false;
                return true;
            } catch(err) {
                this.onDataError(err);
            }

            this.saving = false;
            return false;
        },
        async validateUploadFile(file) {
            if (file.size > 10222279) {
                notifier.showError(this.$t('general.UploadErrorFileSize'));
                return false;
            }

            return true;
        },
        async onUploaded(e) {
            if (e.value.length !== 0) {
                const error = await this.validateUploadFile(e.value[0]);
                if (!error)
                    return;

                this.floorPlan.fileUploaded = e.value[0];
                this.floorPlan.imageFloorUrl = URL.createObjectURL(e.value[0]);
            }
        },
        async onDelete() {
            this.isConfirmDialogActive = true;
            const response = await showConfirmDialog({
                title: this.$t('general.delete'),
                message: `${this.$t('locations.imageTagger.deleteDevice')}?`,
                btnType: 'default',
                okButton: this.$t('general.proceed'),
                cancelButton: this.$t('general.cancel'),
            });

            setTimeout(() => {this.isConfirmDialogActive = false}, 100);
            if (!response) return;

            const updated = {
                ...this.selected,
                xcoordinate: null,
                ycoordinate: null
            };
            this.$emit('update-device', updated);
            this.selected = null;
        },
        onDeleteFloorPlan() {
            this.$emit('reset-devices');
        },
        onOptionChanged(e) {
            if (!e || !e.addedItems || !e.addedItems.length) return;

            this.listSelection = e.addedItems[0];

            this.showPopup = false;
            this.cursorStyle = 'crosshair';
        },
        startDrag(device, event) {
            if (!this.hasEditPermissions) return;

            const svgNode = this.svgSelection.node();

            this.isDragging = true;

            const drag = d3.drag()
                .on("drag", (event) => {
                    const transform = d3.zoomTransform(svgNode);

                    // координаты курсора
                    const [px, py] = d3.pointer(event, svgNode);

                    // screen -> world
                    const x = (px - transform.x) / transform.k;
                    const y = (py - transform.y) / transform.k;

                    device.xcoordinate = Math.max(0, Math.min(1, x / this.img.w));
                    device.ycoordinate = Math.max(0, Math.min(1, y / this.img.h));
                })
                .on("end", () => {
                    this.isDragging = false;
                    this.$emit("update-device", device);
                });

            d3.select(event.currentTarget).call(drag);
            this.selected = null;
        },
        onMouseOverTag(tag) {
            if (this.isDragging) return;

            this.selected = tag;
        },
        createTag(event) {
            if (!this.listSelection) return;

            const svg = this.svgSelection.node();
            const transform = d3.zoomTransform(svg);

            const pt = d3.pointer(event, svg);

            const x = (pt[0] - transform.x) / transform.k;
            const y = (pt[1] - transform.y) / transform.k;

            const updated = {
                ...this.listSelection,
                xcoordinate: x / this.img.w,
                ycoordinate: y / this.img.h
            };

            this.$emit("update-device", updated);
            this.listSelection = null;
            this.cursorStyle = 'move';
        },
        zoomToPoint(scaleFactor) {
            const svg = this.svgSelection;
            const zoom = this.zoomBehavior;

            const rect = this.$refs.svg.getBoundingClientRect();
            const center = [rect.width / 2, rect.height / 2];

            svg.transition().call(
                zoom.scaleBy,
                scaleFactor,
                center
            );
        },
        fitToScreen(animated = true) {
            if (!this.$refs.svg || !this.svgSelection || !this.zoomBehavior) return;

            const svg = this.svgSelection;
            const zoom = this.zoomBehavior;

            const rect = this.$refs.svg.getBoundingClientRect();
            const scale = Math.min(rect.width / this.img.w, rect.height / this.img.h);

            this.minScale = scale;
            zoom.scaleExtent([this.minScale, this.maxScale]);

            const tx = (rect.width - this.img.w * scale) / 2;
            const ty = (rect.height - this.img.h * scale) / 2;

            const transform = d3.zoomIdentity
                .translate(tx, ty)
                .scale(scale);

            if (animated) {
                svg.transition()
                    .duration(200)
                    .ease(d3.easeCubicOut)
                    .call(zoom.transform, transform);
            } else {
                svg.call(zoom.transform, transform);
            }
        },
        updateBounds() {
            const zoom = this.zoomBehavior;
            const rect = this.$refs.svg.getBoundingClientRect();

            zoom.translateExtent([[0, 0], [this.img.w, this.img.h]]);
            zoom.extent([[0, 0], [rect.width, rect.height]]);
        },

        handleClickOutside(event) {
            if (this.isConfirmDialogActive) return;
            if (!this.selected) return;

            // проверяем, что клик был вне модального окна
            const modal = this.$refs.modalInfo;
            if (modal && !modal.contains(event.target)) {
                this.selected = null;
            }
        },
        initD3() {
            if (!this.$refs.svg || !this.$refs.viewport) return;

            const svg = d3.select(this.$refs.svg);
            const viewport = d3.select(this.$refs.viewport);

            this.svgSelection = svg;
            this.viewportSelection = viewport;

            const zoom = d3.zoom()
                .scaleExtent([this.minScale, this.maxScale])
                // ограничение панорамирования
                .translateExtent([
                    [0, 0],
                    [this.img.w, this.img.h]
                ])
                // ограничение зоны зума
                .extent([
                    [0, 0],
                    [this.img.w, this.img.h]
                ])
                .on("zoom", (event) => {
                    this.currentTransform = event.transform;
                    viewport.attr("transform", event.transform);
                });

            svg.call(zoom);

            // отключаем dblclick zoom
            svg.on("dblclick.zoom", null);

            this.zoomBehavior = zoom;

            const initial = d3.zoomIdentity
                .translate(0, 0)
                .scale(1);

            svg.call(zoom.transform, initial);
        },
        async loadImage() {
            return new Promise((resolve, reject) => {
                if (!this.floorPlan.imageFloorUrl) {
                    resolve();
                    return;
                }

                const img = new Image();
                img.src = this.floorPlan.imageFloorUrl;

                img.onload = () => {
                    this.img = {
                        w: img.naturalWidth,
                        h: img.naturalHeight
                    };

                    this.$nextTick(() => {
                        this.updateBounds();
                        this.fitToScreen(false);
                        resolve();
                    });
                };

                img.onerror = (error) => {
                    reject(error);
                };
            });
        },
        onResize() {
            if (!this.$refs.svg || !this.svgSelection) return;

            this.updateBounds();
            this.fitToScreen(true);
        },
        initResizeObserver() {
            if (!this.$refs.container) return;

            let timeout;

            this.resizeObserver = new ResizeObserver(() => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    this.onResize();
                }, 50);
            });

            this.resizeObserver.observe(this.$refs.container);
        },
        centerInitialZoom() {
            if (!this.$refs.svg || !this.svgSelection) return;

            const svg = this.svgSelection;
            const zoom = this.zoomBehavior;

            const rect = this.$refs.svg.getBoundingClientRect();

            if (this.img.w === 0 || this.img.h === 0) return;

            const scale = Math.min(
                rect.width / this.img.w,
                rect.height / this.img.h
            );

            const tx = (rect.width - this.img.w * scale) / 2;
            const ty = (rect.height - this.img.h * scale) / 2;

            svg.call(
                zoom.transform,
                d3.zoomIdentity.translate(tx, ty).scale(scale)
            );
        },
        async waitForInitialization() {
            // ждём, пока загрузится изображение
            while (!this.img.w || !this.img.h) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }

            // ждём, пока установится currentTransform
            while (!this.currentTransform) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        },
        async initViewer() {
            await this.$nextTick();

            if (!this.floorPlan.imageFloorUrl || !this.$refs.svg) return;

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            await this.initD3();
            await this.loadImage();
            this.initResizeObserver();

            await this.$nextTick();
            this.centerInitialZoom();
            this.initialized = true;
        }
    },
    mounted() {
        this.onWindowResize = () => {
            this.fitToScreen(true);
        };

        window.addEventListener("resize", this.onWindowResize);

        // обработчик клика для закрытия модального окна
        document.addEventListener('click', this.handleClickOutside);

        // инициализация D3 и загрузка изображения
        this.initViewer();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.onWindowResize);
        document.removeEventListener('click', this.handleClickOutside);

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    watch: {
        '$route.query': {
            immediate: true,
            async handler(params) {
                if (!params.deviceID) return;

                const id = Number(params.deviceID);
                const device = this.devices.find(el => el.id === id);

                if (!device) return;

                await this.waitForInitialization();

                this.selected = device;
            }
        },
        'floorPlan.imageFloorUrl': {
            async handler(value) {
                if (value) {
                    await this.initViewer();
                } else {
                    this.selected = null;
                    this.initialized = false;

                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();
                    }
                }
            }
        }
    }
  };
</script>

<style lang="scss">
.plan-upload-floor {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;

    .dx-fileuploader-files-container {
        display: none;
    }

    .upload-title {
        padding: 5px 16px;
        border-radius: 4px;
        background-color: #fffce5;
        font-weight: 600;
        text-align: center;
    }

    .btn-upload {
        width: 400px;
    }

    .emptyData {
        color: #999;
        font-size: 17px;
    }
}

.plan-image {
    display: flex;
    flex-direction: row;
    height: 100%;
    column-gap: 10px;

    .actions {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;

        .zommer {
            display: flex;
            flex-direction: column;
            row-gap: 10px;
            margin: auto;
        }
    }


    .consumables {
        padding-right: 7px;
    }
    .dx-datagrid .dx-row > td {
        padding: 2px !important;
    }
}
.floorPlan {
    width: 100%;
    height: 100%;
}
.viewer {
    position: relative;
    width: 100%;
    height: 100%;
}
.scene-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.tag-svg {
    stroke: rgba(0, 0, 0, 0.7);
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
    pointer-events: none;
    cursor: grab;
}
.tag-svg-hit-area {
    fill: transparent;
    stroke: transparent;
    pointer-events: all;
    cursor: grab;
}
.point--animated {
    animation: point-pulse 2s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
}
@keyframes point-pulse {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.3);
    }
}
.tag-svg.ok {
  fill: rgb(0,128,0);
}
.tag-svg.warning {
  fill: rgb(255,165,0);
}
.tag-svg.error {
  fill: rgb(255,0,0);
}
.tag-svg.not-serviced {
  fill: rgb(170,170,170);
}

.modal-device-info {
    position: absolute;
    z-index: 10;
    width: 350px;
    background: white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    border-radius: 6px;
}
.device-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 7px 7px 0 7px;

    .dx-button-content {
        padding: 4px !important;
    }
}

.add-device-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;

    pointer-events: auto;
    background-color: hsla(0, 0%, 100%, .8);

    display: flex;
    align-items: center;
    justify-content: center;
}
.add-device-window {
    width: 500px;
    max-height: 400px;

    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    -webkit-box-shadow: 0 6px 12px rgba(0, 0, 0, .175);
    box-shadow: 0 6px 12px rgba(0, 0, 0, .175);

    display: flex;
    flex-direction: column;

    overflow: hidden;
}
.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    padding: 6px 20px;

    border-bottom: 1px solid #eee;
}
.popup-title {
    color: #333;
    font-weight: 400;
    font-size: 20px;
    font-family: Helvetica Neue, Segoe UI, helvetica, verdana, sans-serif;
    line-height: 1.35715;
}
.popup-content {
    flex: 1;
    min-height: 0;

    display: flex;
    padding: 20px;
}
.popup-list {
    width: 100%;
}
.device-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.status-dot {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    flex-shrink: 0;

    border: 2px solid rgba(0,0,0,0.7);
}
.status-dot.ok {
    background: rgb(0,128,0);
}
.status-dot.warning {
    background: rgb(255,165,0);
}
.status-dot.error {
    background: rgb(255,0,0);
}
.status-dot.not-serviced {
    background: rgb(170,170,170);
}
</style>
